
import { createPermanentTemplate, getAddressLookupData, getSuburb, getProductModel } from '@/api';
import { getReferencesParamsWrapper } from '@/helpers/booking';
import { clearPhoneNumber } from '@/utils/InputProcessingUtilities';

import BackButton from '@/components/UI/backButton/BackButton.vue';
import Button from '@/components/UI/button/Button.vue';
import LookupSuburb from '@/components/UI/lookups/LookupSuburb.vue';
import Autocomplete from '@/components/jobBooking/Goods/Autocomplete.vue';
import CustomerDetailsFieldGroup from '@/components/fieldGroups/CustomerDetailsFieldGroup.vue';

import * as Sentry from '@sentry/vue';
import * as routeNames from '@/router/routeNames';

import { UserDataAdvancedJB, User } from '@/models/main';
import { AddressBookLookupData } from '@/models/addressBook';
import { PermanentCreatePayload, PermanentFormStop, PermanentFormProduct } from '@/models/permanentJob';
import { installationOptions, returnOptions } from '@/utils/PortalConstants';

import { Component, Vue } from 'vue-property-decorator';
import { mapState } from 'vuex';
import { notificationErrorText } from '@/utils/PortalConstants';

@Component({
  components: {
    BackButton,
    Button,
    LookupSuburb,
    Autocomplete,
    CustomerDetailsFieldGroup,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
  data() {
    return {
      installationOptions,
      returnOptions,
    };
  },
})
export default class PermanentJobBooking extends Vue {
  public routeNames = routeNames;
  private loading = false;
  private user: User;
  private stop3 = false;
  private errorFromAPIResponse = '';
  private indefinite = true;

  // Address book
  private addresses: AddressBookLookupData[] = [];
  private addressesWithoutWarehousesNames: string[] = [];
  private addressesWarehousesNames: string[] = [];

  // Suburb + Model autocomplete
  private autocompleteSuburb = getSuburb;
  private autocompleteModel = getProductModel;

  private userDataDefault: UserDataAdvancedJB = {
    customerCode: '',
    caller: '',
    phone_number: '',
    reference1: '',
    reference2: '',
    reference3: '',
    ref1_prompt: 'Reference 1',
    ref2_prompt: 'Reference 2',
    ref3_prompt: 'Reference 3',
    ref1_required: false,
    ref2_required: false,
    ref3_required: false,
  };

  private userData: UserDataAdvancedJB = { ...this.userDataDefault };

  private schedule = {
    start_on: '',
    finish_on: '',
    no_of_times: -1,
    auto_driver_option: 'prealloc',
    holiday_rule: 'P',
    display_type: 'C',
    driver: null as number | null,
    rep: '',
    rep_since: '',
    require_pod: false,
    scan: false,
    print_on_manifest: false,
    will_call: false,
    notes: '',
    not_on1: '',
    not_on2: '',
    not_on3: '',
    not_on4: '',
    also_on1: '',
    also_on2: '',
    also_on3: '',
    also_on4: '',
    required_on_monday: true,
    required_on_tuesday: true,
    required_on_wednesday: true,
    required_on_thursday: true,
    required_on_friday: true,
    required_on_saturday: false,
    required_on_sunday: false,
  };

  private autoDriverOptions = [
    { text: 'Pre-allocate', value: 'prealloc' },
    { text: 'Allocate', value: 'allocate' },
    { text: 'Complete', value: 'complete' },
  ];

  private holidayRuleOptions = [
    { text: 'Cancel', value: 'C' },
    { text: 'Proceed', value: 'P' },
    { text: 'Next day', value: 'N' },
    { text: 'Move week', value: 'M' },
  ];

  private displayTypeOptions = [
    { text: 'City', value: 'C' },
    { text: 'Street', value: 'S' },
    { text: 'Name', value: 'N' },
    { text: 'Suite', value: 'U' },
    { text: 'Etc1', value: 'E' },
    { text: 'Etc2', value: 'F' },
  ];

  // Stops (following JBAdvancedStops pattern, extended for permanent)
  private stops: PermanentFormStop[] = [this.createEmptyStop(1), this.createEmptyStop(2), this.createEmptyStop(3)];

  // Products (inline table, following Goods pattern)
  private get goodsHeaders() {
    const headers: Array<{ text: string; value: string; sortable?: boolean; width?: string }> = [
      { text: 'Model', value: 'model', width: '200px' },
      { text: 'Description', value: 'description' },
      { text: 'Serial number', value: 'serial', width: '150px' },
      { text: 'Quantity', value: 'quantity', width: '140px' },
      { text: 'Installation', value: 'installation', width: '180px' },
      { text: 'Return option', value: 'returns', width: '160px' },
    ];
    if (this.stop3) {
      headers.push({ text: 'Route', value: 'route', sortable: false, width: '140px' });
    }
    headers.push({ text: '', value: 'actions', sortable: false, width: '50px' });
    return headers;
  }

  private get routeOptions() {
    return [
      { text: 'Stop 1 → 2', value: '1-2' },
      { text: 'Stop 1 → 3', value: '1-3' },
      { text: 'Stop 2 → 3', value: '2-3' },
    ];
  }
  private productIdCounter = 1;
  private products: PermanentFormProduct[] = [this.createEmptyProduct()];

  // --- Stop helpers ---

  private createEmptyStop(number: number): PermanentFormStop {
    return {
      id: number,
      name: '',
      address_1: '',
      address_2: '',
      suburb: '',
      suburbId: -1,
      state: '',
      postCode: '',
      warehouse: false,
      notesInstruction: '',
      notesName: '',
      notesPhone: '',
      notesEmail: '',
      required_at: '',
      latest_time: '',
      attributes: [],
      services: [],
      mon_driver: null,
      tue_driver: null,
      wed_driver: null,
      thu_driver: null,
      fri_driver: null,
      sat_driver: null,
      sun_driver: null,
      addToAddressBook: false,
    };
  }

  // --- Product helpers ---

  private createEmptyProduct(): PermanentFormProduct {
    return {
      id: this.productIdCounter++,
      model: '',
      description: '',
      serial: '',
      quantity: 1,
      weight: 0,
      from_stop: 1,
      to_stop: 2,
      installation: '',
      returns: '',
      product_id: 0,
    };
  }

  private addProduct(): void {
    this.products.push(this.createEmptyProduct());
  }

  private removeProduct(idx: number): void {
    this.products.splice(idx, 1);
  }

  private changeModel(stop: number, item: any): void {
    this.products[stop].model = item.name;
    this.products[stop].product_id = item.product_id;
  }

  private clearModel(stop: number): void {
    this.products[stop].model = '';
    this.products[stop].product_id = 0;
  }

  private incrementQuantity(item: PermanentFormProduct): void {
    item.quantity++;
  }

  private decrementQuantity(item: PermanentFormProduct): void {
    if (item.quantity > 1) {
      item.quantity--;
    }
  }

  private changeRoute(item: PermanentFormProduct, value: string): void {
    const [from, to] = value.split('-').map(Number);
    item.from_stop = from;
    item.to_stop = to;
  }

  // --- Customer code / address book ---

  private customerLookupToken = 0;

  private setCustomerCode(customerCode: string): void {
    // Invalidate any in-flight customer lookup so stale responses can't apply.
    const token = ++this.customerLookupToken;
    this.userData.customerCode = customerCode;
    this.addresses = [];
    this.addressesWarehousesNames = [];
    this.addressesWithoutWarehousesNames = [];
    this.userData.ref1_prompt = '';
    this.userData.ref2_prompt = '';
    this.userData.ref3_prompt = '';
    this.userData.ref1_required = false;
    this.userData.ref2_required = false;
    this.userData.ref3_required = false;
    if (!customerCode) {
      return;
    }
    const refsBuffer: Partial<UserDataAdvancedJB> = {};
    getReferencesParamsWrapper(this.userData.customerCode, refsBuffer).then(() => {
      // Only apply if the customer hasn't changed since we kicked off the request.
      if (token === this.customerLookupToken) {
        Object.assign(this.userData, refsBuffer);
      }
    });
    this.getAddressBookWrapper(customerCode, token);
  }

  private getAddressBookWrapper(code: string, token: number): void {
    getAddressLookupData(code)
      .then((res: AddressBookLookupData[]) => {
        // Drop stale responses from a previous customer.
        if (token !== this.customerLookupToken) {
          return;
        }
        this.addresses = res;
        this.addressesWarehousesNames = [];
        this.addressesWithoutWarehousesNames = [];
        res.forEach((item: AddressBookLookupData) => {
          const formatted = item.suburb ? `${item.name}, ${item.suburb.name}` : item.name;
          if (item.warehouse) {
            this.addressesWarehousesNames.push(formatted);
          } else {
            this.addressesWithoutWarehousesNames.push(formatted);
          }
        });
      })
      .catch(err => {
        Sentry.captureException(err);
      });
  }

  private changeAddressBook(address: string, stop: number): void {
    if (address && stop && this.addresses.length >= 1) {
      const chosenAddress = this.addresses.filter(
        item => (item.suburb ? `${item.name}, ${item.suburb.name}` : item.name).trim() === address.trim(),
      );
      this.stops[stop - 1].name = address.trim().substring(0, 30);
      if (chosenAddress.length >= 1) {
        this.stops[stop - 1].name = chosenAddress[0].name.trim().substring(0, 30);
        this.stops[stop - 1].address_1 = chosenAddress[0].line1 ? chosenAddress[0].line1.substring(0, 30) : '';
        this.stops[stop - 1].address_2 = chosenAddress[0].line2 ? chosenAddress[0].line2.substring(0, 30) : '';
        this.stops[stop - 1].notesName = chosenAddress[0].note_name || '';
        this.stops[stop - 1].notesPhone = chosenAddress[0].note_phone || '';
        this.stops[stop - 1].notesInstruction = chosenAddress[0].note_text || '';
        this.stops[stop - 1].warehouse = chosenAddress[0].warehouse;
        if (chosenAddress[0].suburb) {
          const lookup = {
            suburb: chosenAddress[0].suburb.name,
            state: chosenAddress[0].suburb.state,
            postcode: chosenAddress[0].suburb.postcode,
            suburbId: chosenAddress[0].suburb.id,
          };
          this.changeSuburb(stop - 1, lookup);
        }
      }
    }
  }

  private keyPressedAddressBookName(key: any, stop: number): void {
    if (key.target.value) {
      this.stops[stop - 1].name = key.target.value.trim();
    }
  }

  // --- Suburb ---

  private changeSuburb(stop: number, item: any): void {
    this.stops[stop].suburb = item.suburb || item.name;
    this.stops[stop].suburbId = item.suburbId || item.id;
    this.stops[stop].postCode = item.postcode;
    this.stops[stop].state = item.state;
  }

  private clearSuburb(stop: number): void {
    this.stops[stop].suburb = '';
    this.stops[stop].suburbId = -1;
    this.stops[stop].postCode = '';
    this.stops[stop].state = '';
  }

  // --- Warehouse toggle ---

  private toggleWarehouse(stopIdx: number): void {
    // Clear address/contact info so the previous customer-address fields
    // are not silently sent as part of a warehouse stop (or vice versa).
    const stop = this.stops[stopIdx];
    stop.name = '';
    stop.address_1 = '';
    stop.address_2 = '';
    stop.suburb = '';
    stop.suburbId = -1;
    stop.state = '';
    stop.postCode = '';
    stop.notesName = '';
    stop.notesPhone = '';
    stop.notesEmail = '';
    stop.notesInstruction = '';
  }

  // --- Stop 3 ---

  private addStop3(): void {
    this.stop3 = true;
  }

  private removeStop3(): void {
    this.stop3 = false;
    // Vue 2 can't detect direct index assignment; splice is reactive.
    this.stops.splice(2, 1, this.createEmptyStop(3));
    // Any products routed via stop 3 are now invalid: reset to 1 → 2.
    this.products.forEach(p => {
      if (p.from_stop === 3 || p.to_stop === 3) {
        p.from_stop = 1;
        p.to_stop = 2;
      }
    });
  }

  // --- Phone / misc ---

  private onlyNumberAndSpaceBlur(): void {
    this.userData.phone_number = clearPhoneNumber(this.userData.phone_number);
  }

  private onlyNumberAndSpace($event: KeyboardEvent): void {
    const keyCode = $event.keyCode ? $event.keyCode : $event.which;
    if ((keyCode < 48 || keyCode > 57) && keyCode !== 32 && keyCode !== 40 && keyCode !== 41) {
      $event.preventDefault();
    }
  }

  private onIndefiniteChange(): void {
    this.schedule.no_of_times = this.indefinite ? -1 : 1;
  }

  private formChanged(): void {
    this.errorFromAPIResponse = '';
  }

  // --- Build payload ---

  private buildJob(): PermanentCreatePayload {
    const visibleStops = this.stop3 ? 3 : 2;
    const payload: PermanentCreatePayload = {
      schema_version: '1.0',
      customer_code: this.userData.customerCode,
      caller: this.userData.caller,
      phone: this.userData.phone_number,
      REF1: this.userData.reference1,
      REF2: this.userData.reference2,
      REF3: this.userData.reference3,
      auto_driver_option: this.schedule.auto_driver_option as 'prealloc' | 'allocate' | 'complete',
      holiday_rule: this.schedule.holiday_rule as 'C' | 'P' | 'N' | 'M',
      no_of_times: this.schedule.no_of_times,
      require_pod: this.schedule.require_pod,
      scan: this.schedule.scan,
      print_on_manifest: this.schedule.print_on_manifest,
      will_call: this.schedule.will_call,
      required_on_sunday: this.schedule.required_on_sunday,
      required_on_monday: this.schedule.required_on_monday,
      required_on_tuesday: this.schedule.required_on_tuesday,
      required_on_wednesday: this.schedule.required_on_wednesday,
      required_on_thursday: this.schedule.required_on_thursday,
      required_on_friday: this.schedule.required_on_friday,
      required_on_saturday: this.schedule.required_on_saturday,
      stops: this.stops.slice(0, visibleStops).map((stop, idx) => ({
        name: stop.name,
        suburb: stop.suburb,
        state: stop.state,
        postcode: stop.postCode,
        address_line1: stop.address_1 || '',
        address_line2: stop.address_2 || '',
        warehouse: stop.warehouse,
        attributes: stop.attributes,
        number: idx + 1,
        required_at: stop.required_at ? stop.required_at + ':00' : '',
        latest_time: stop.latest_time ? stop.latest_time + ':00' : '',
        ...(stop.notesInstruction ? { note_text: stop.notesInstruction } : {}),
        ...(stop.notesName ? { note_name: stop.notesName } : {}),
        ...(stop.notesPhone ? { note_phone: stop.notesPhone } : {}),
        ...(stop.notesEmail ? { note_email: stop.notesEmail } : {}),
        ...(stop.services.length ? { services: stop.services } : {}),
        ...(stop.mon_driver ? { mon_driver: stop.mon_driver } : {}),
        ...(stop.tue_driver ? { tue_driver: stop.tue_driver } : {}),
        ...(stop.wed_driver ? { wed_driver: stop.wed_driver } : {}),
        ...(stop.thu_driver ? { thu_driver: stop.thu_driver } : {}),
        ...(stop.fri_driver ? { fri_driver: stop.fri_driver } : {}),
        ...(stop.sat_driver ? { sat_driver: stop.sat_driver } : {}),
        ...(stop.sun_driver ? { sun_driver: stop.sun_driver } : {}),
      })),
      products: this.products.map(p => ({
        serial: p.serial,
        model: p.model,
        description: p.description,
        quantity: p.quantity,
        weight: p.weight,
        from_stop: p.from_stop,
        to_stop: p.to_stop,
        installation: p.installation || '',
        returns: p.returns ? p.returns.toUpperCase() : '',
      })),
    };

    // Optional fields
    if (this.schedule.start_on) {
      payload.start_on = this.schedule.start_on;
    }
    if (this.schedule.finish_on) {
      payload.finish_on = this.schedule.finish_on;
    }
    if (this.schedule.driver) {
      payload.driver = this.schedule.driver;
    }
    if (this.schedule.display_type) {
      payload.display_type = this.schedule.display_type as any;
    }
    if (this.schedule.rep) {
      payload.rep = this.schedule.rep;
    }
    if (this.schedule.rep_since) {
      payload.rep_since = this.schedule.rep_since;
    }
    if (this.schedule.notes) {
      payload.notes = this.schedule.notes;
    }
    if (this.schedule.not_on1) {
      payload.not_on1 = this.schedule.not_on1;
    }
    if (this.schedule.not_on2) {
      payload.not_on2 = this.schedule.not_on2;
    }
    if (this.schedule.not_on3) {
      payload.not_on3 = this.schedule.not_on3;
    }
    if (this.schedule.not_on4) {
      payload.not_on4 = this.schedule.not_on4;
    }
    if (this.schedule.also_on1) {
      payload.also_on1 = this.schedule.also_on1;
    }
    if (this.schedule.also_on2) {
      payload.also_on2 = this.schedule.also_on2;
    }
    if (this.schedule.also_on3) {
      payload.also_on3 = this.schedule.also_on3;
    }
    if (this.schedule.also_on4) {
      payload.also_on4 = this.schedule.also_on4;
    }

    return payload;
  }

  // --- Submit ---

  private bookJob(): void {
    const form = this.$refs.form as HTMLFormElement;
    if (form.validate()) {
      if (this.products.length === 0 || !this.products.some(p => !!p.model)) {
        this.errorFromAPIResponse = 'Please add at least one product before booking.';
        return;
      }
      if (this.products.some(p => !p.model)) {
        this.errorFromAPIResponse = 'Each product row must have a model. Remove any blank rows before booking.';
        return;
      }
      this.bookValidatedJob();
    } else {
      this.$nextTick(() => {
        const el = this.$el.querySelector('.v-messages.error--text:first-of-type') as HTMLElement;
        if (el) {
          this.$vuetify.goTo(el, { offset: 100 });
        }
      });
    }
  }

  private bookValidatedJob(): void {
    this.loading = true;
    const payload = this.buildJob();

    createPermanentTemplate(payload)
      .then(res => {
        if (res.failed && res.failed.length > 0 && (!res.booked || res.booked.length === 0)) {
          this.errorFromAPIResponse = res.failed[0].error_reason || 'Booking failed. Check the data and try again.';
          this.$nextTick(() => {
            const el = this.$el.querySelector('.error-response') as HTMLElement;
            if (el) {
              this.$vuetify.goTo(el, { offset: 100 });
            }
          });
          return;
        }

        this.$notify({ group: 'network', clean: true });
        this.$notify({
          group: 'network',
          text: 'Permanent job booked successfully',
          type: 'success',
          duration: 3000,
        });
        this.$router.push({ name: routeNames.PERMANENT_JOBS_LIST });
      })
      .catch((res: any) => {
        if (res?.response?.status === 400 && res?.response?.data?.failed?.length > 0) {
          this.errorFromAPIResponse = res.response.data.failed[0].error_reason || 'Validation error';
          Sentry.withScope(scope => {
            scope.setLevel('warning');
            scope.setExtra('error_reason', res.response?.data.failed[0].error_reason);
            Sentry.captureException(res);
          });
          this.$nextTick(() => {
            const el = this.$el.querySelector('.error-response') as HTMLElement;
            if (el) {
              this.$vuetify.goTo(el, { offset: 100 });
            }
          });
        } else {
          this.$notify({
            group: 'network',
            title: 'Unknown error during permanent job booking',
            text: notificationErrorText,
            type: 'warn',
          });
          Sentry.withScope(scope => {
            scope.setLevel('error');
            Sentry.captureException(res);
          });
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  // --- Clear form ---

  private clearForm(): void {
    // Invalidate any in-flight customer lookup so its response can't repopulate cleared fields.
    ++this.customerLookupToken;
    this.userData = { ...this.userDataDefault };
    this.schedule = {
      start_on: '',
      finish_on: '',
      no_of_times: -1,
      auto_driver_option: 'prealloc',
      holiday_rule: 'P',
      display_type: 'C',
      driver: null,
      rep: '',
      rep_since: '',
      require_pod: false,
      scan: false,
      print_on_manifest: false,
      will_call: false,
      notes: '',
      not_on1: '',
      not_on2: '',
      not_on3: '',
      not_on4: '',
      also_on1: '',
      also_on2: '',
      also_on3: '',
      also_on4: '',
      required_on_monday: true,
      required_on_tuesday: true,
      required_on_wednesday: true,
      required_on_thursday: true,
      required_on_friday: true,
      required_on_saturday: false,
      required_on_sunday: false,
    };
    this.indefinite = true;
    this.stop3 = false;
    this.productIdCounter = 1;
    this.stops = [this.createEmptyStop(1), this.createEmptyStop(2), this.createEmptyStop(3)];
    this.products = [this.createEmptyProduct()];
    this.errorFromAPIResponse = '';
    this.addresses = [];
    this.addressesWarehousesNames = [];
    this.addressesWithoutWarehousesNames = [];

    const form = this.$refs.form as HTMLFormElement;
    if (form) {
      form.resetValidation();
    }
  }
}
